import { useCallback, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { isDebugModeEnabled } from './oktaAuth';
import { useDebugAuth } from './debugAuth';

/**
 * Custom hook that proxies useOktaAuth and returns debug auth when in debug mode
 * This allows all components to use the same interface regardless of auth mode
 * Components don't need to know about debug mode at all - all debug-specific
 * behavior is handled internally by this hook
 */
export function useAuth() {
  const isDebugMode = isDebugModeEnabled();
  
  // Get both auth contexts
  const oktaAuthContext = useOktaAuth();
  let debugAuthContext = null;
  
  try {
    debugAuthContext = useDebugAuth();
  } catch {
    // Debug auth context is not available if not in debug mode
    debugAuthContext = null;
  }
  
  // Return the appropriate context based on debug mode
  const authContext = isDebugMode && debugAuthContext 
    ? debugAuthContext 
    : oktaAuthContext;
    
  // Memoize the getUser function to prevent unnecessary re-renders
  const getUser = useCallback(async () => {
    if (isDebugMode && authContext.authState?.user) {
      return authContext.authState.user as any;
    }
    return authContext.oktaAuth?.getUser?.() || null;
  }, [isDebugMode, authContext.authState?.user, authContext.oktaAuth]);
  
  // Memoize the signOut function to prevent unnecessary re-renders
  const signOut = useCallback(async (options?: any) => {
    if (isDebugMode) {
      // In debug mode, just reload the page
      window.location.reload();
      return;
    }
    return authContext.oktaAuth?.signOut?.(options);
  }, [isDebugMode, authContext.oktaAuth]);
  
  // Create a unified interface that handles debug mode internally
  const unifiedAuth = useMemo(() => ({
    authState: authContext.authState,
    oktaAuth: {
      ...authContext.oktaAuth,
      getUser,
      signOut
    }
  }), [authContext.authState, authContext.oktaAuth, getUser, signOut]);
  
  return isDebugMode ? unifiedAuth : authContext;
}
